import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LanguageIcon from '@mui/icons-material/Language';
import { Box, Fab, Grid, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import makeStyles from '@mui/styles/makeStyles';
import classnames from 'classnames';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ExtendedComment } from "../../../types/comments";
import { SaveDashboardItemType } from '../../../types/common';
import { CompanyFile, Dashboard, DashboardDocument, DashboardScreenshot, DashboardSelection } from '../../../types/files';
import { getFileIcon, getFolderFileIcon } from '../../atoms/folders/files';
import DashboardTitleEditor from '../../molecules/dashboard-header/DashboardTitleEditor';
import DashboardFileComponent from '../../molecules/dashboard-item-types/DashboardFileComponent';
import DashboardSelectionComponent from '../../molecules/dashboard-item-types/DashboardSelectionComponent';
import DashboardItem from "../../molecules/dashboard-items/DashboardItem";

const useStyles = makeStyles((theme) => ({
    title: {
        marginBottom: 8,
        display: '-webkit-box',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '80%',
        maxHeight: '90%',
        padding: 0,
    },
    card: {
        height: 60,
        display: "flex",
        alignItems: "center"
    },
    documentContainer: {
        height: '100%',
    },
    sourceElement: {
        display: "-webkit-box",
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        wordBreak: 'break-word',
        padding: 0,
        maxWidth: "calc(100% - 55px)",
        cursor: 'pointer',
        "&:hover": {
            textDecoration: "underline"
        }
    },
    fileIcon: {
        marginRight: 10,
        marginLeft: 10,
    },
    titleContainer: {
        display: 'flex',
        padding: "8px 20px",
        alignItems: 'center',
        cursor: 'pointer',
        justifyContent: 'space-between'
    },
    separator: {
        marginLeft: 10,
    },
    fabScrollLeft: {
        position: "fixed",
        left: "calc(10% - 26px)",
        top: "calc(50% - 26px)",
        height: 52,
        width: 52,
        borderRadius: "50%",
        zIndex: 0,
        backgroundColor: 'white',
        color: '#048290',
        border: '1px solid #048290',
        '& svg': {
            fill: '#048290',
            width: 26,
            height: 30
        }
    },
    fabScrollRight: {
        position: "fixed",
        right: "calc(10% - 26px)",
        top: "calc(50% - 26px)",
        height: 52,
        width: 52,
        borderRadius: "50%",
        zIndex: 0,
        backgroundColor: 'white',
        color: '#048290',
        border: '1px solid #048290',
        '& svg': {
            fill: '#048290',
            width: 26,
            height: 30
        }
    },
    rightPanel: {
        backgroundColor: '#F8F9FA',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 32,
        gap: 32
    },
    dialogFooter: {
        marginTop: -50,
        marginBottom: 10
    }
}));

interface Props {
    dashboard: Dashboard | undefined,
    item: DashboardDocument | DashboardScreenshot | DashboardSelection | undefined,
    itemIndex: number,
    itemsCount: number,
    isOpen: boolean,
    onSummaryUpdated: (item: DashboardDocument | DashboardScreenshot | DashboardSelection) => void,
    onItemTitleUpdated: (item: DashboardDocument | DashboardScreenshot | DashboardSelection) => void,
    onClose: () => void,
    files: CompanyFile[],
    onNext: () => void,
    onBack: () => void,
    preselectedCommentId?: string;
    externalComments: ExtendedComment[];
    readonly?: boolean
}

const DashboardItemDetailsDialog = ({ externalComments, item, itemIndex, itemsCount, dashboard, isOpen, onSummaryUpdated, onItemTitleUpdated, onClose, files, onNext, onBack, preselectedCommentId, readonly = false}: Props) => {
    const classes = useStyles();
    const history = useHistory();
    const [dashboardElement, setDashboardElement] = useState<DashboardDocument | DashboardScreenshot | DashboardSelection>();
    const [isNavigationEnabled] = useState<boolean>(true);


    useEffect(() => {
        if (item) {
            setDashboardElement(item);
        }
        // eslint-disable-next-line
    }, [item]);

    useEffect(() => {
        const keyDownHandler = (event: any) => {
            const isFocusOnInput = event.target.nodeName === "INPUT" ||
                event.target.nodeName === "TEXTAREA" ||
                event.target.isContentEditable;

            if (event.key === 'ArrowRight' && itemIndex !== itemsCount - 1 && isNavigationEnabled && !isFocusOnInput) {
                onNext();
            } else if (event.key === 'ArrowLeft' && itemIndex > 0 && isNavigationEnabled && !isFocusOnInput) {
                onBack();
            }
        };
        document.removeEventListener('keydown', keyDownHandler);
        document.addEventListener('keydown', keyDownHandler);

        return () => {
            document.removeEventListener('keydown', keyDownHandler);
        }

        // eslint-disable-next-line
    }, [item, isNavigationEnabled]);

    const getDashboardItemComponent = (item: DashboardDocument | DashboardScreenshot | DashboardSelection | undefined) => {
        if (!item) {
            return;
        }
        let itemComponent: any;
        if ("searchTerms" in item) {
            itemComponent = <DashboardFileComponent
                dashboardId={dashboard?.id!}
                dashboardItemType={SaveDashboardItemType.Page}
                data={item as DashboardDocument}
                comments={[]}
                selectedCommentId={undefined}
                selectedCommentParentId={''}
                onCommentAdded={()=> {}}
            />;
        } else if ("selectedText" in item) {
            itemComponent = <DashboardSelectionComponent
                dashboardId={dashboard?.id!}
                dashboardItemType={SaveDashboardItemType.Text}
                data={item as DashboardSelection}
                comments={[]}
                selectedCommentId={undefined}
                selectedCommentParentId={''}
                onCommentAdded={()=> {}}
            />;
        }

        return <DashboardItem id={item?.id!}>
            <div style={{ height: "100%" }}>
                {itemComponent}
            </div>
        </DashboardItem>;
    }

    const getSourceElement = () => {
        if (item?.key) {
            const keyFileName = item.key.split('/').slice(-1)[0];
            const document = files.find(e => e.key === item.key);
            return (<Box
                sx={{ display: 'flex', width: '100%', alignItems: "center" }}
                onClick={(e) => {
                    if (document) {
                        e.preventDefault();
                        e.stopPropagation();
                        onSourceClick();
                    }
                }}>
                {document ? getFolderFileIcon(document, classes.fileIcon, classes.fileIcon) : getFileIcon(keyFileName, classes.fileIcon)}
                {document ? <Typography className={classes.sourceElement}>{document?.name}</Typography> : <Typography className={classes.sourceElement}>{keyFileName}</Typography>}
            </Box>);
        } else {
            return <Box
                sx={{ display: 'flex', width: 'calc(100% - 50px)' }}
            >
                <LanguageIcon className={classes.fileIcon} />
                <Typography
                    className={classes.sourceElement}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onSourceClick();
                    }}
                >{(item as DashboardScreenshot)?.url}</Typography>
            </Box>;
        }
    }

    const onSourceClick = () => {
        if (!item) {
            return;
        }

        const pageNumber = ((item as any)?.page || (item as any)?.page === 0) ?
            (item as any)?.page :
            (item as any)?.pages && (item as any)?.pages[0];

        const documentId = item.key;
        const url = (item as DashboardScreenshot)?.url;

        if (documentId && (pageNumber || pageNumber === 0)) {
            history.push(`/folders?documentId=${encodeURIComponent(documentId)}&page=${pageNumber}`);
        } else if (url) {
            window.open(url);
        }
    }

    const onUpdateItemTitle = (value: string): boolean => {
        if (dashboardElement) {
            const newItem = {
                ...dashboardElement,
                title: value
            };
            setDashboardElement(newItem);
            onItemTitleUpdated(newItem);
        }

        return true;
    }

    return (
        <Dialog
            sx={{
                '& .MuiDialog-paper': {
                    minWidth: '80%',
                    height: '95%',
                    position: 'relative',
                    overflow: 'unset',
                    borderRadius: "10px"
                }
            }}
            maxWidth="xs"
            open={isOpen}
            onBackdropClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
            onClose={() => { onClose(); }}
        >
            <Grid container className={classes.titleContainer}>
                <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                    <DashboardTitleEditor
                        title={dashboardElement?.title || ''}
                        variant="h6"
                        onUpdate={onUpdateItemTitle}
                        style={{ fontSize: 20, fontWeight: 700, margin: 16 }}
                        readonly={readonly}
                    />
                </Grid>
                <CloseIcon onClick={onClose} style={{color: '#AEB5BC', cursor: 'pointer'}}/>
            </Grid>
            <DialogContent dividers sx={{ padding: "0 20px" }}>
                <Grid container display="flex" flexDirection="column" sx={{ height: '100%' }}>
                    <Grid item sx={{ height: '100%' }}>
                        <Grid container sx={{ height: '100%' }}>
                            <Grid
                                item
                                xs={12}
                                p="8px"
                                className={classnames(classes.documentContainer)}
                                sx={{ height: '100%' }}
                            >
                                <Grid container display="flex" flexDirection="column" height="100%">
                                    <div style={{ height: '90%' }} data-dashboarditemid={dashboardElement?.id}>
                                        {getDashboardItemComponent(dashboardElement)}
                                    </div>
                                    <Grid item p={1} display="flex" alignItems="center" sx={{ height: '25%' }}>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions className={classes.dialogFooter}>
                <Grid item sx={{ marginRight: "10px", marginLeft: "10px", display: 'flex', flex: "1", alignItems: 'center' }}>
                    {getSourceElement()}
                </Grid>
            </DialogActions>
            {
                itemIndex > 0 &&
                <Fab
                    size="large"
                    variant="extended"
                    className={classes.fabScrollLeft}
                    disabled={!isNavigationEnabled}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onBack();
                    }}
                >
                    <KeyboardArrowLeftIcon fontSize="large" color="info" />
                </Fab>
            }
            {
                itemIndex < itemsCount - 1 &&
                <Fab
                    size="large"
                    variant="extended"
                    className={classes.fabScrollRight}
                    disabled={!isNavigationEnabled}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onNext();
                    }}
                >
                    <KeyboardArrowRightIcon fontSize="large" color="info" />
                </Fab>
            }
        </Dialog>
    );
}

export default DashboardItemDetailsDialog;
